import type { SitePhotoRemarkOption, SitePhotoSheetOption, SiteReportDraft } from '@epc/contracts';
import { Camera, Image as ImageIcon, MapPinOff, Pencil, Trash2, Video } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../../ui/button';
import { Input, Select } from '../../ui/form';
import type { PhotoSource } from './offline/photo-sync';
import type { LocalPhoto } from './offline/store';
import { PhotoMarkupDialog } from './PhotoMarkupDialog';

type DraftPhoto = SiteReportDraft['photos'][number];

/** Подпись состояния фото/видео: понятно, ушло ли оно на сервер и что делать, если нет. */
export function photoStateLabel(photo: DraftPhoto, local: LocalPhoto | undefined, online: boolean) {
  const noun = photo.kind === 'video' ? 'Видео' : 'Снимок';
  if (photo.fileId) return 'Загружено на сервер';
  if (!local) return `${noun} не найдено на устройстве — снимите ещё раз`;
  return online ? 'Ждёт отправки' : 'Хранится на телефоне, уйдёт при появлении связи';
}

function Thumb({ blob, label, kind }: { blob: Blob | undefined; label: string; kind: DraftPhoto['kind'] }) {
  const url = useMemo(() => (blob ? URL.createObjectURL(blob) : null), [blob]);
  useEffect(() => () => (url ? URL.revokeObjectURL(url) : undefined), [url]);
  if (!url) return <div className="size-16 shrink-0 rounded-md border border-line bg-tile" aria-hidden />;
  if (kind === 'video') {
    return (
      <video
        src={url}
        className="size-16 shrink-0 rounded-md border border-line object-cover"
        muted
        playsInline
        preload="metadata"
        aria-label={label}
      />
    );
  }
  return (
    <img src={url} alt={label} className="size-16 shrink-0 rounded-md border border-line object-cover" />
  );
}

/** Фото одной работы: съёмка камерой, выбор из галереи, подпись, привязка к листу/замечанию, удаление. */
export function PhotoStrip({
  lineId,
  photos,
  local,
  editable,
  online,
  sheets = [],
  remarks = [],
  onAdd,
  onCaption,
  onLink,
  onMarkup,
  onForgetLocation,
  onRemove,
}: {
  lineId: string;
  photos: DraftPhoto[];
  local: LocalPhoto[];
  editable: boolean;
  online: boolean;
  sheets?: SitePhotoSheetOption[];
  remarks?: SitePhotoRemarkOption[];
  onAdd: (lineId: string, file: File, source: PhotoSource) => void;
  onCaption: (clientPhotoId: string, caption: string) => void;
  onLink: (clientPhotoId: string, link: { sheetId: string | null; remarkId: string | null }) => void;
  onMarkup?: (clientPhotoId: string, prepared: { blob: Blob; sha256: string; size: number }) => void;
  /** Отозвать согласие: место удаляется из записи (МП-07) */
  onForgetLocation?: (clientPhotoId: string) => void;
  onRemove: (clientPhotoId: string) => void;
}) {
  const camera = useRef<HTMLInputElement>(null);
  const gallery = useRef<HTMLInputElement>(null);
  const videoCamera = useRef<HTMLInputElement>(null);
  const videoGallery = useRef<HTMLInputElement>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [marking, setMarking] = useState<string | null>(null);
  const mine = photos.filter((p) => p.lineId === lineId);
  const markingPhoto = marking ? mine.find((p) => p.clientPhotoId === marking) : null;
  const markingLocal = marking ? local.find((p) => p.clientPhotoId === marking) : null;
  const pick = (source: PhotoSource) => (e: React.ChangeEvent<HTMLInputElement>) => {
    for (const file of Array.from(e.target.files ?? [])) onAdd(lineId, file, source);
    e.target.value = '';
  };
  const showLinks = sheets.length > 0 || remarks.length > 0 || mine.some((p) => p.sheetId || p.remarkId);
  return (
    <div className="mt-4">
      <h4 className="mb-2 text-sm font-semibold">Фото и видео · {mine.length}</h4>
      {mine.length ? (
        <ul className="mb-3 grid gap-3">
          {mine.map((photo) => {
            const row = local.find((p) => p.clientPhotoId === photo.clientPhotoId);
            const isVideo = photo.kind === 'video';
            return (
              <li key={photo.clientPhotoId} className="flex items-start gap-3">
                <Thumb
                  blob={row?.blob}
                  label={isVideo ? 'Видео работы' : 'Снимок работы'}
                  kind={photo.kind ?? 'photo'}
                />
                <div className="min-w-0 flex-1">
                  <Input
                    aria-label={isVideo ? 'Подпись к видео' : 'Подпись к фото'}
                    className="!h-11"
                    maxLength={2000}
                    placeholder={isVideo ? 'Подпись: что на видео' : 'Подпись: что на снимке'}
                    value={photo.caption}
                    disabled={!editable}
                    onChange={(e) => onCaption(photo.clientPhotoId, e.target.value)}
                  />
                  {showLinks ? (
                    <div className="mt-2 grid gap-2 sm:grid-cols-2">
                      <Select
                        aria-label="Лист ПСД"
                        className="!h-11"
                        value={photo.sheetId ?? ''}
                        disabled={!editable}
                        onChange={(e) =>
                          onLink(photo.clientPhotoId, {
                            sheetId: e.target.value || null,
                            remarkId: photo.remarkId ?? null,
                          })
                        }
                      >
                        <option value="">Лист ПСД не указан</option>
                        {sheets.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.label}
                          </option>
                        ))}
                        {photo.sheetId && !sheets.some((s) => s.id === photo.sheetId) ? (
                          <option value={photo.sheetId}>Лист уже выбран</option>
                        ) : null}
                      </Select>
                      <Select
                        aria-label="Замечание"
                        className="!h-11"
                        value={photo.remarkId ?? ''}
                        disabled={!editable}
                        onChange={(e) =>
                          onLink(photo.clientPhotoId, {
                            sheetId: photo.sheetId ?? null,
                            remarkId: e.target.value || null,
                          })
                        }
                      >
                        <option value="">Замечание не указано</option>
                        {remarks.map((r) => (
                          <option key={r.id} value={r.id}>
                            {r.label}
                          </option>
                        ))}
                        {photo.remarkId && !remarks.some((r) => r.id === photo.remarkId) ? (
                          <option value={photo.remarkId}>Замечание уже выбрано</option>
                        ) : null}
                      </Select>
                    </div>
                  ) : null}
                  <p className="mt-1 text-xs text-ink-2">
                    {isVideo ? 'Видео · ' : ''}
                    {photoStateLabel(photo, row, online)}
                    {photo.source === 'gallery' ? ' · из галереи, место и время съёмки не подтверждены' : ''}
                    {photo.locationConsent && photo.location
                      ? ` · место сохранено (${photo.location.lat.toFixed(5)}, ${photo.location.lng.toFixed(5)})`
                      : photo.source === 'camera'
                        ? ' · место не сохраняли'
                        : ''}
                  </p>
                </div>
                {editable ? (
                  <div className="flex shrink-0 flex-col gap-2">
                    {onForgetLocation && photo.locationConsent && photo.location ? (
                      <Button
                        className="!h-11 !min-w-11"
                        variant="ghost"
                        aria-label="Убрать место съёмки"
                        title="Убрать место съёмки из записи"
                        icon={<MapPinOff className="size-4" />}
                        onClick={() => onForgetLocation(photo.clientPhotoId)}
                      />
                    ) : null}
                    {onMarkup && !isVideo && row?.blob && !photo.fileId ? (
                      <Button
                        className="!h-11 !min-w-11"
                        variant="ghost"
                        aria-label="Пометить фото"
                        icon={<Pencil className="size-4" />}
                        onClick={() => setMarking(photo.clientPhotoId)}
                      />
                    ) : null}
                    {confirming === photo.clientPhotoId ? (
                      <Button
                        className="!h-11 !min-w-11"
                        variant="danger"
                        onClick={() => {
                          setConfirming(null);
                          onRemove(photo.clientPhotoId);
                        }}
                      >
                        Убрать
                      </Button>
                    ) : (
                      <Button
                        className="!h-11 !min-w-11"
                        variant="ghost"
                        aria-label="Убрать фото"
                        icon={<Trash2 className="size-4" />}
                        onClick={() => setConfirming(photo.clientPhotoId)}
                      />
                    )}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}
      {markingPhoto && markingLocal?.blob && onMarkup ? (
        <PhotoMarkupDialog
          blob={markingLocal.blob}
          onClose={() => setMarking(null)}
          onSave={(prepared) => {
            onMarkup(markingPhoto.clientPhotoId, prepared);
            setMarking(null);
          }}
        />
      ) : null}
      {editable ? (
        <div className="flex flex-wrap gap-2">
          <input
            ref={camera}
            type="file"
            accept="image/*"
            capture="environment"
            className="sr-only"
            tabIndex={-1}
            aria-label="Снять фото камерой"
            onChange={pick('camera')}
          />
          <input
            ref={gallery}
            type="file"
            accept="image/*"
            multiple
            className="sr-only"
            tabIndex={-1}
            aria-label="Выбрать фото из галереи"
            onChange={pick('gallery')}
          />
          <input
            ref={videoCamera}
            type="file"
            accept="video/*"
            capture="environment"
            className="sr-only"
            tabIndex={-1}
            aria-label="Снять видео камерой"
            onChange={pick('camera')}
          />
          <input
            ref={videoGallery}
            type="file"
            accept="video/mp4,video/webm,video/quicktime"
            className="sr-only"
            tabIndex={-1}
            aria-label="Выбрать видео из галереи"
            onChange={pick('gallery')}
          />
          <Button
            className="!h-11"
            icon={<Camera className="size-4" />}
            onClick={() => camera.current?.click()}
          >
            Снять фото
          </Button>
          <Button
            className="!h-11"
            variant="ghost"
            icon={<ImageIcon className="size-4" />}
            onClick={() => gallery.current?.click()}
          >
            Из галереи
          </Button>
          <Button
            className="!h-11"
            variant="ghost"
            icon={<Video className="size-4" />}
            onClick={() => videoCamera.current?.click()}
          >
            Снять видео
          </Button>
          <Button
            className="!h-11"
            variant="ghost"
            icon={<Video className="size-4" />}
            onClick={() => videoGallery.current?.click()}
          >
            Видео из галереи
          </Button>
        </div>
      ) : null}
    </div>
  );
}
